import * as A from 'fp-ts/lib/Array';
import { pipe } from 'fp-ts/lib/function';
import { isEmpty } from 'lodash';

import { isNotEmpty } from '@utils/isNotEmpty';
import {
  isAfterStartDate,
  isBeforeEndDate,
  LeadershipDate,
} from '@utils/leadershipUtils';

import { ROTARACT_ADVISOR } from '@domui-domain/clubs/constants';
import {
  CLUB_EXECUTIVE_SECRETARY,
  DISTRICT_EXECUTIVE_SECRETARY,
  MEMBERSHIP_TYPE_HONORARY,
  MEMBERSHIP_TYPE_HONORARY_ROTARACTOR,
  MEMBERSHIP_TYPE_MEMBER,
  MEMBERSHIP_TYPE_ROTARACTOR,
  MULTI_DISTRICT_EXECUTIVE_SECRETARY,
} from '@domui-domain/districts/constants';
import { areLeadershipAffiliations } from '@domui-utils/getUserClubs';
import {
  Affiliation,
  ClubAffiliation as DominoClubAffiliation,
} from '@domui-utils/types';

import { AddLog } from '@hooks/logger';

import { ClubAffiliation, LogLevel } from '@typings/operations';

export const doesUserHaveMembership = (
  clubAffiliation: ClubAffiliation[]
): boolean =>
  pipe(
    clubAffiliation,

    A.filter(item => 'type' in item),
    A.isNonEmpty
  );

export const isActiveLeadership = (item: LeadershipDate): boolean => {
  if (isNotEmpty(item?.startDate)) {
    if (isEmpty(item?.endDate)) {
      return isAfterStartDate(item);
    }
    return isAfterStartDate(item) && isBeforeEndDate(item);
  }
  return false;
};

export const isActiveAffiliation = (affiliations: Affiliation[]) => {
  return affiliations?.some(affiliation =>
    isActiveLeadership(affiliation as LeadershipDate)
  );
};

export const verifyIfUserIsMemberOrExecutiveSecretary = (
  clubAffiliations: DominoClubAffiliation[],
  individualId: string,
  addLog: AddLog
): boolean => {
  const allClubsAndDistricts = clubAffiliations?.filter(({ type }) =>
    ['District', 'Rotary Club', 'Rotaract Club'].includes(type)
  );
  const isMemberOrExecutiveSecretary = allClubsAndDistricts?.some(
    ({ affiliation }) =>
      !isEmpty(affiliation) &&
      Array.isArray(affiliation) &&
      (areLeadershipAffiliations(affiliation, MEMBERSHIP_TYPE_MEMBER) ||
        areLeadershipAffiliations(affiliation, MEMBERSHIP_TYPE_HONORARY) ||
        areLeadershipAffiliations(affiliation, MEMBERSHIP_TYPE_ROTARACTOR) ||
        areLeadershipAffiliations(
          affiliation,
          MEMBERSHIP_TYPE_HONORARY_ROTARACTOR
        ) ||
        areLeadershipAffiliations(affiliation, CLUB_EXECUTIVE_SECRETARY) ||
        areLeadershipAffiliations(affiliation, DISTRICT_EXECUTIVE_SECRETARY) ||
        areLeadershipAffiliations(
          affiliation,
          MULTI_DISTRICT_EXECUTIVE_SECRETARY
        ) ||
        areLeadershipAffiliations(affiliation, ROTARACT_ADVISOR)) &&
      isActiveAffiliation(affiliation)
  );

  addLog({
    level: LogLevel.Info,
    message: `memberSearch::verifyIfUserIsMemberOrExecutiveSecretary, Is Individual ${individualId} a member or secretary ${isMemberOrExecutiveSecretary}`,
  });

  return isMemberOrExecutiveSecretary;
};

export const verifyIfUserIsMember = (
  clubAffiliations: DominoClubAffiliation[],
  individualId: string,
  addLog: AddLog
): boolean => {
  const allClubsAndDistricts = clubAffiliations?.filter(({ type }) =>
    ['District', 'Rotary Club', 'Rotaract Club'].includes(type)
  );
  const isMember = allClubsAndDistricts?.some(
    ({ affiliation }) =>
      !isEmpty(affiliation) &&
      Array.isArray(affiliation) &&
      (areLeadershipAffiliations(affiliation, MEMBERSHIP_TYPE_MEMBER) ||
        areLeadershipAffiliations(affiliation, MEMBERSHIP_TYPE_HONORARY) ||
        areLeadershipAffiliations(affiliation, MEMBERSHIP_TYPE_ROTARACTOR) ||
        areLeadershipAffiliations(
          affiliation,
          MEMBERSHIP_TYPE_HONORARY_ROTARACTOR
        )) &&
      isActiveAffiliation(affiliation)
  );

  addLog({
    level: LogLevel.Info,
    message: `memberSearch::verifyIfUserIsMember, Is Individual ${individualId} a member ${isMember}`,
  });

  return isMember;
};

export const verifyIfUserIsNonMemberLeader = (
  clubAffiliations: DominoClubAffiliation[],
  individualId: string,
  addLog: AddLog
): boolean => {
  const allClubsAndDistricts = clubAffiliations?.filter(({ type }) =>
    ['District', 'Rotary Club', 'Rotaract Club'].includes(type)
  );
  const isNonMemberLeader = allClubsAndDistricts?.some(
    ({ affiliation }) =>
      !isEmpty(affiliation) &&
      Array.isArray(affiliation) &&
      (areLeadershipAffiliations(affiliation, CLUB_EXECUTIVE_SECRETARY) ||
        areLeadershipAffiliations(affiliation, ROTARACT_ADVISOR) ||
        areLeadershipAffiliations(affiliation, DISTRICT_EXECUTIVE_SECRETARY)) &&
      isActiveAffiliation(affiliation)
  );

  addLog({
    level: LogLevel.Info,
    message: `memberSearch::verifyIfUserIsNonMemberLeader, Is Individual ${individualId} a non-member leader ${isNonMemberLeader}`,
  });

  return isNonMemberLeader;
};
